const SITE_URL = "https://frontrangecargobikes.com";

// Default Article image used when caller doesn't supply one.
// Google's Article rich-result spec marks `image` as a Required property —
// emitting the OG fallback keeps Search Console clean instead of spreading the
// field conditionally and tripping "Missing field 'image'" warnings.
const DEFAULT_ARTICLE_IMAGE = `${SITE_URL}/images/og-image.jpg`;

/**
 * Serialize a JSON-LD payload safely for inline emission inside a
 * <script type="application/ld+json"> tag.
 *
 * `JSON.stringify` does NOT escape characters that can break out of a
 * <script> tag or break HTML parsing:
 *   - "<" / ">"   — a value containing "</script>" would terminate the tag
 *   - "&"         — defensive (not strictly required inside <script>, but
 *                   safer if the blob is ever copied into other contexts)
 *   - U+2028/2029 — line separators that are valid JSON but invalid in JS
 *                   string literals; some legacy parsers choke on them
 *
 * This is the standard JSON-LD XSS hardening pattern and matches what
 * Next.js's <Script> and Remix's <script> helpers do internally.
 */
function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

interface BreadcrumbItem {
  name: string;
  href: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      ...items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: item.name,
        item: `${SITE_URL}${item.href}`,
      })),
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  );
}

interface ArticleJsonLdProps {
  title: string;
  description: string;
  url: string;
  publishedAt: string;
  modifiedAt?: string;
  image?: string;
}

export function ArticleJsonLd({
  title,
  description,
  url,
  publishedAt,
  modifiedAt,
  image,
}: ArticleJsonLdProps) {
  const fullUrl = `${SITE_URL}${url}`;
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url: fullUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": fullUrl,
    },
    datePublished: publishedAt,
    // Google strongly recommends dateModified; default to publishedAt when
    // caller hasn't tracked a separate modified timestamp.
    dateModified: modifiedAt ?? publishedAt,
    // image is a Required property for Article rich results. Always emit it.
    image: image ? `${SITE_URL}${image}` : DEFAULT_ARTICLE_IMAGE,
    author: {
      "@type": "Organization",
      name: "Front Range Cargo Bikes",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Front Range Cargo Bikes",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/images/og-image.jpg`,
      },
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  );
}

interface FAQItem {
  question: string;
  answer: string;
}

export function FAQPageJsonLd({ faqs }: { faqs: FAQItem[] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
    />
  );
}
